import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {OutputType} from 'common/src/modules/nodes/nodes.types';
import {BaseCardScratchpadState} from '../../scratchpad/baseCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';

const deleteOutputsToolSchema = z.object({
  outputIds: z.array(z.number()).describe('The ids of the outputs to delete'),
});

export default function deleteOutputsTool() {
  async function execute(
    params: ToolExecuteParams<typeof deleteOutputsToolSchema, BaseCardScratchpadState>,
  ) {
    const {outputIds} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad(agentScratchpad);

    const fallbackOutputId = validatedScratchpad.card.outputs.find(
      (output) => output.type === OutputType.Fallback,
    )?.id;

    const regularOutputs = validatedScratchpad.card.outputs.filter(
      (output) => !outputIds.includes(output.id) && output.type === OutputType.Regular,
    );

    //For some reason all outputs were deleted, return error
    if (regularOutputs.length === 0) {
      return toolResult(
        JSON.stringify({
          status: 'error',
          message: 'At least one non-fallback output must be present',
        }),
      );
    }

    //If only fallback output was asked to be deleted, return error
    if (outputIds.length === 1 && outputIds[0] === fallbackOutputId) {
      return toolResult(
        JSON.stringify({
          status: 'error',
          message: 'The Fallback output is mandatory and cannot be deleted.',
        }),
      );
    }

    validatedScratchpad.card.outputs = validatedScratchpad.card.outputs.filter(
      (output) => !outputIds.includes(output.id),
    );

    let responseMessage = 'Outputs deleted successfully';

    if (!!fallbackOutputId && outputIds.includes(fallbackOutputId)) {
      responseMessage =
        'Deleted all selected outputs except for the Fallback output, which is mandatory and must remain.';
    }

    return toolResult(JSON.stringify({status: 'success', message: responseMessage}));
  }

  return tool<typeof deleteOutputsToolSchema, BaseCardScratchpadState>({
    name: V2CardAgentToolName.DeleteOutputs,
    description: 'Delete outputs from a card by outputId',
    displayName: 'Deleting outputs',
    inputSchema: deleteOutputsToolSchema,
    execute,
  });
}
